import { z } from "zod";

/**
 * Schema for the runtime configuration contract published at /config.js.
 *
 * This schema is evaluated at app boot; its failure modes are user-visible
 * errors rather than silently-degraded UI.
 */
const AppConfigSchema = z.object({
    msal: z.object({
        clientId: z.string().min(1, "msal.clientId must be a non-empty string"),
        authority: z.string().url("msal.authority must be an absolute URL"),
        apiScope: z.string().min(1, "msal.apiScope must be a non-empty string"),
    }),
    /**
     * Absolute origin of the Galileo app (scheme + host, no trailing path),
     * e.g. https://galileo.sagefreight.review. Einstein and Galileo are
     * separate origins in every deployed environment (Einstein owns
     * neweinstein.sagefreight.{review,com}; Galileo owns
     * galileo.sagefreight.{review,com}), so the Galileo origin cannot be
     * derived from window.location and must be supplied per environment.
     *
     * Required and validated at boot: a missing/invalid origin previously
     * caused cross-app links to fall back to window.location.origin, which
     * lands inside Einstein's own PWA scope and re-launches Einstein instead
     * of opening Galileo. Failing fast here makes that misconfiguration
     * impossible to ship silently.
     */
    galileoOrigin: z.string().url("galileoOrigin must be an absolute URL (e.g. https://galileo.sagefreight.review)"),
    /**
     * Which deployment environment is serving this PWA. Drives the in-app
     * environment marker (a red "QA" beside the brand). This is a runtime
     * concern, not a build-time flag: one web image is promoted QA → prod,
     * so only the ConfigMap-mounted config.js distinguishes them.
     *
     * Strictly enumerated so a typo fails fast at boot (a precise
     * loadAppConfig error) rather than silently mislabeling the
     * environment. Optional — production overlays omit it (absent ⇒ no
     * marker), and adding a new environment is a deliberate code change
     * that also decides that environment's marker.
     */
    environment: z.enum(["qa", "prod"]).optional(),
    /**
     * Web Push configuration. Optional so environments without push
     * provisioned still boot — the notifications toggle simply stays hidden
     * when `vapidPublicKey` is absent. The matching private key lives only in
     * the backend (Key Vault); the public key is safe to ship to the browser.
     */
    push: z
        .object({
            vapidPublicKey: z.string().min(1, "push.vapidPublicKey must be a non-empty string"),
        })
        .optional(),
});

export type AppConfig = z.infer<typeof AppConfigSchema>;

/**
 * Loads and validates the runtime configuration from `window.__APP_CONFIG__`.
 *
 * Throws a descriptive `Error` when the contract is not satisfied — the
 * message lists every invalid field so the operator can fix the ConfigMap
 * (or local placeholder) without guessing.
 */
export function loadAppConfig(): AppConfig {
    if (typeof window === "undefined") {
        throw new Error(
            "Runtime configuration cannot be loaded: `window` is undefined. " +
                "authConfig.loadAppConfig() must be called in a browser context.",
        );
    }

    const raw = window.__APP_CONFIG__;
    if (raw === undefined || raw === null) {
        throw new Error(
            "Runtime configuration missing at /config.js: window.__APP_CONFIG__ is undefined. " +
                "Verify that /config.js is served before the app bundle and sets window.__APP_CONFIG__.",
        );
    }

    const result = AppConfigSchema.safeParse(raw);
    if (!result.success) {
        const details = result.error.issues
            .map((issue) => {
                const path = issue.path.length > 0 ? issue.path.join(".") : "(root)";
                return `  - ${path}: ${issue.message}`;
            })
            .join("\n");
        throw new Error(
            "Runtime configuration at /config.js is invalid. " +
                "The following fields failed validation:\n" +
                details,
        );
    }

    return result.data;
}
